import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTitle } from '@hooks';

import { Box, Typography } from '@mui/material';
import {
  CandidateDetailsHeader,
  CandidateDetailsActionButton,
  CandidateDetailsContent,
  CandidateDetailsPageLayout,
  PageLoader,
  ClientFeedbackModal,
  ClientRescheduleCancelModal,
  CandidateAskQuestionModal,
} from '@components';

import {
  candidateDetailsSelectors,
  clearCandidateDetails,
  fetchCandidateDetails,
  setCandidateDetailsAction,
} from '@redux/candidateDetails';

import { CandidateDetailsAction, SearchParams } from '@constants';
import { isStatusForbidden, isStatusIdle, isStatusLoading } from '@utils';
import { NotAllowed } from '@components';

export const CandidateDetailsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const setPageTitle = useTitle();
  const selectedAccountId = useAppSelector((state) => state.accounts.selected);

  const { apiStatus, data } = useAppSelector(
    candidateDetailsSelectors.getCandidateDetailsData,
  );

  useEffect(() => {
    if (!candidateId) return;

    dispatch(fetchCandidateDetails(candidateId));

    return () => {
      dispatch(clearCandidateDetails());
    };
  }, [candidateId, dispatch, selectedAccountId]);

  useEffect(() => {
    const action = searchParams.get(
      SearchParams.ACTION,
    ) as CandidateDetailsAction;

    if (Object.values(CandidateDetailsAction).includes(action)) {
      dispatch(setCandidateDetailsAction(action));
    }
  }, [candidateId, dispatch, searchParams, selectedAccountId]);

  useEffect(() => {
    if (data?.profile?.firstName && data?.profile.lastName) {
      setPageTitle(`${data.profile.firstName} ${data?.profile.lastName}`);
    }
  }, [setPageTitle, apiStatus]);

  if (isStatusForbidden(apiStatus)) {
    return <NotAllowed />;
  }

  if (isStatusLoading(apiStatus) || isStatusIdle(apiStatus)) {
    return <PageLoader />;
  }

  if (!data) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
      >
        <Typography variant="h1">Candidate not found</Typography>
      </Box>
    );
  }

  return (
    <>
      <CandidateDetailsPageLayout
        headerComponent={<CandidateDetailsHeader data={data} />}
        contentComponent={<CandidateDetailsContent data={data} />}
        footerComponent={
          <CandidateDetailsActionButton data={data} sx={{ px: 2, py: 1.5 }} />
        }
      />
      <ClientFeedbackModal />
      <ClientRescheduleCancelModal />
      <CandidateAskQuestionModal />
    </>
  );
};
